import React from 'react';
import { useTranslation } from 'react-i18next';

import { ActionButtonModal } from 'src/legacy/components/Modals';
import { useManageCompanyClientsMutation } from 'src/services/api/companiesApi';

type PendingRemoval = {
  companyId: string;
  clientId: string;
  companyName: string;
  isLastCompany: boolean;
} | null;

export type RemoveClientWithWarningParams = {
  companyId: string;
  clientId: string;
  companyName: string;
  isLastCompany: boolean;
};

type UseDataLossWarningOptions = {
  onSuccess?: (params: RemoveClientWithWarningParams) => void;
};

export type UseDataLossWarningReturn = {
  removeClientWithWarning: (params: RemoveClientWithWarningParams) => void;
  cancelRemoval: () => void;
  pendingRemoval: PendingRemoval;
  DataLossWarningModal: React.FC;
};

function DataLossWarningModalComponent({
  pendingRemoval,
  onCancel,
  onConfirm,
}: {
  pendingRemoval: PendingRemoval;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();

  if (!pendingRemoval) return null;

  return (
    <ActionButtonModal
      open
      onClose={onCancel}
      positiveAction
      onPositiveActionPerformed={onConfirm}
      title={t(($) => $.messages.remove_company_warning.title)}
      positiveActionText={t(($) => $.common.confirm)}
      mainComponent={
        pendingRemoval.isLastCompany
          ? t(($) => $.messages.remove_company_warning.description_last, {
              companyName: pendingRemoval.companyName,
            })
          : t(($) => $.messages.remove_company_warning.description_single, {
              companyName: pendingRemoval.companyName,
            })
      }
    />
  );
}

export const useDataLossWarning = (
  options: UseDataLossWarningOptions = {},
): UseDataLossWarningReturn => {
  const { onSuccess } = options;
  const onSuccessRef = React.useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const [manageCompanyClients] = useManageCompanyClientsMutation();
  const [pendingRemoval, setPendingRemoval] =
    React.useState<PendingRemoval>(null);

  const removeClientWithWarning = React.useCallback(
    (params: RemoveClientWithWarningParams) => {
      // Show the confirmation modal immediately instead of making an API call first
      setPendingRemoval(params);
    },
    [],
  );

  const confirmRemoval = React.useCallback(async () => {
    if (!pendingRemoval) return;
    const confirmedParams = pendingRemoval;
    setPendingRemoval(null);
    try {
      const result = await manageCompanyClients({
        companyId: confirmedParams.companyId,
        addClientIds: [],
        removeClientIds: [confirmedParams.clientId],
        confirmDataLoss: true,
      }).unwrap();
      if (!result.removeClientErrors?.length) {
        onSuccessRef.current?.(confirmedParams);
      }
    } catch {
      // Network/server errors are handled by companiesApi's onQueryStarted
    }
  }, [manageCompanyClients, pendingRemoval]);

  const cancelRemoval = React.useCallback(() => {
    setPendingRemoval(null);
  }, []);

  const DataLossWarningModal: React.FC = React.useCallback(
    () => (
      <DataLossWarningModalComponent
        pendingRemoval={pendingRemoval}
        onCancel={cancelRemoval}
        onConfirm={confirmRemoval}
      />
    ),
    [pendingRemoval, cancelRemoval, confirmRemoval],
  );

  return {
    removeClientWithWarning,
    cancelRemoval,
    pendingRemoval,
    DataLossWarningModal,
  };
};
